import { useState, useMemo } from 'react'
import { useMagoMaster } from '../hooks/useMagoMaster'
import { useDestMaster } from '../hooks/useDestMaster'
import { useAuth } from '../contexts/AuthContext'
import MagoMasterForm from './MagoMasterForm'
import { DEVICE_STATUS_MAP } from '../types/master'
import type { MagoMasterResponse, DeviceStatus } from '../types/master'

type SortField = keyof MagoMasterResponse
type SortOrder = 'asc' | 'desc'

export default function MagoMasterList() {
  const { devices, loading, error, refetch, deleteDevice } = useMagoMaster()
  const { destinations } = useDestMaster()
  const { user } = useAuth()
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<MagoMasterResponse | null>(null)
  const [sortField, setSortField] = useState<SortField>('mago_id')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [destFilter, setDestFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null)

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const filterDevices = (devices: MagoMasterResponse[]) => {
    return devices.filter(device => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const searchFields = [
          device.mago_id,
          device.dest_name,
          device.lot_id,
          device.group_id,
          device.product_id,
          device.sim_id,
        ]
        const matches = searchFields.some(
          field => field && field.toLowerCase().includes(searchLower)
        )
        if (!matches) return false
      }

      // Destination filter
      if (destFilter && device.dest_id.toString() !== destFilter) {
        return false
      }

      // Status filter
      if (statusFilter && device.status.toString() !== statusFilter) {
        return false
      }

      return true
    })
  }

  const sortDevices = (devices: MagoMasterResponse[]) => {
    return devices.sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })
  }

  const filteredAndSortedDevices = useMemo(() => {
    if (!devices) return []
    const filtered = filterDevices(devices)
    return sortDevices(filtered)
  }, [devices, searchTerm, destFilter, statusFilter, sortField, sortOrder])

  const handleEdit = (device: MagoMasterResponse) => {
    setEditingItem(device)
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteDevice(id)
      setShowDeleteConfirm(null)
    } catch (error) {
      console.error('Failed to delete device:', error)
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingItem(null)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setEditingItem(null)
    refetch()
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted" />
    return sortOrder === 'asc' ? (
      <i className="fas fa-sort-up text-primary" />
    ) : (
      <i className="fas fa-sort-down text-primary" />
    )
  }

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const getStatusBadge = (status: DeviceStatus) => {
    const statusInfo = DEVICE_STATUS_MAP[status]
    return <span className={`badge bg-${statusInfo.color}`}>{statusInfo.label}</span>
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">読み込み中...</span>
        </div>
        <p className="mt-2 text-muted">端末を読み込んでいます...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-triangle me-2" />
        端末読み込みエラー: {error}
      </div>
    )
  }

  return (
    <>
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-3">
              <label className="form-label">端末を検索</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="IDや名前で検索..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">運用先</label>
              <select
                className="form-select"
                value={destFilter}
                onChange={e => setDestFilter(e.target.value)}
              >
                <option value="">全ての運用先</option>
                {destinations.map(dest => (
                  <option key={dest.id} value={dest.id.toString()}>
                    {dest.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">ステータス</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">全てのステータス</option>
                {Object.entries(DEVICE_STATUS_MAP).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label text-muted">
                {devices.length}件中{filteredAndSortedDevices.length}件を表示
              </label>
            </div>
            <div className="col-md-3">
              {user?.role === 'admin' && (
                <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                  <i className="fas fa-plus me-2" />
                  新規追加
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-microchip me-2" />
            端末マスター
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0">
              <thead className="table-dark">
                <tr>
                  <th scope="col" className="sortable" onClick={() => handleSort('mago_id')}>
                    端末ID {getSortIcon('mago_id')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('dest_name')}>
                    運用先 {getSortIcon('dest_name')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('status')}>
                    ステータス {getSortIcon('status')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('lot_id')}>
                    Lot ID {getSortIcon('lot_id')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('group_id')}>
                    Group ID {getSortIcon('group_id')}
                  </th>
                  <th scope="col" className="sortable" onClick={() => handleSort('product_id')}>
                    Product ID {getSortIcon('product_id')}
                  </th>
                  <th scope="col">SIM ID</th>
                  <th scope="col">LTE Type</th>
                  <th scope="col" className="sortable" onClick={() => handleSort('entry_dt')}>
                    作成日時 {getSortIcon('entry_dt')}
                  </th>
                  <th scope="col" className="text-center">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedDevices.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      <i className="fas fa-info-circle me-2" />
                      検索条件に一致する端末が見つかりません
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedDevices.map(device => (
                    <tr key={device.id}>
                      <td className="fw-bold">{device.mago_id}</td>
                      <td>{device.dest_name || <span className="text-muted">-</span>}</td>
                      <td>{getStatusBadge(device.status as DeviceStatus)}</td>
                      <td>{device.lot_id || <span className="text-muted">-</span>}</td>
                      <td>{device.group_id || <span className="text-muted">-</span>}</td>
                      <td>{device.product_id || <span className="text-muted">-</span>}</td>
                      <td className="font-monospace small">
                        {device.sim_id || <span className="text-muted">-</span>}
                      </td>
                      <td>{device.lte_type || <span className="text-muted">-</span>}</td>
                      <td className="text-nowrap small">{formatDateTime(device.entry_dt)}</td>
                      <td className="text-center">
                        {user?.role === 'admin' ? (
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleEdit(device)}
                              title="編集"
                            >
                              <i className="fas fa-edit" />
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => setShowDeleteConfirm(device.id)}
                              title="削除"
                            >
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleEdit(device)}
                            title="表示"
                          >
                            <i className="fas fa-eye" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <MagoMasterForm
          device={editingItem}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">削除確認</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(null)}
                />
              </div>
              <div className="modal-body">
                この端末を削除してもよろしいですか？この操作は元に戻せません。
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDelete(showDeleteConfirm)}
                >
                  削除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
